'use client';
import React, { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import type { User } from 'firebase/auth';
import { registerDevice } from '@/actions/user_specific';

// requestIdleCallback isn't in Safari; fall back to a short timeout so the
// deferral still works there, just without idle-time scheduling. Paired
// cancel function must match whichever branch scheduled the callback.
const supportsIdleCallback =
  typeof window !== 'undefined' && 'requestIdleCallback' in window;
const scheduleIdle = supportsIdleCallback
  ? window.requestIdleCallback.bind(window)
  : (cb: () => void) => window.setTimeout(cb, 200);
const cancelIdle = supportsIdleCallback
  ? window.cancelIdleCallback.bind(window)
  : (handle: number) => window.clearTimeout(handle);


type AuthUserContextType = {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  device?: {auth_token: string} | null
};

const AuthUserContext = createContext<AuthUserContextType | undefined>(undefined);

function useAuthUser() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [device, setDevice] = useState<{auth_token: string} | null>();

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    // Deferred to idle time so the Firebase Auth SDK — and the auth-state
    // persistence iframe it loads on first use — doesn't compete with
    // LCP-critical resources (video/poster) during initial page load.
    // Firebase auth state (login/logout) still resolves automatically,
    // just a beat later; no user interaction is required to trigger it.
    // See Aug 2026 Lighthouse audit §2.2 (1,872ms critical-path chain).
    const idleHandle = scheduleIdle(async () => {
      if (cancelled) return;
      const [{ onAuthStateChanged }, { getFirebaseAuth }] = await Promise.all([
        import('firebase/auth'),
        import('@/lib/firebase'),
      ]);
      if (cancelled) return;

      unsubscribe = onAuthStateChanged(getFirebaseAuth(), async (firebaseUser) => {
        setUser(firebaseUser);
        setLoading(false);

        const userObj = firebaseUser ? {
          uid: firebaseUser.uid,
          user_id: firebaseUser.uid,
          email_id: firebaseUser.email,
          display_name: firebaseUser.displayName,
          photo_url: firebaseUser.photoURL,
          phoneNumber: firebaseUser.phoneNumber,
          is_email_verified: firebaseUser.emailVerified,
          providerId: firebaseUser.providerId,
        } : undefined;


        if (firebaseUser) {
          // Register device with user info
          const device = await registerDevice(undefined, userObj as any);
          setDevice(device as any);
        } else {
          // Handle user logout or no user state
          console.log('No user is signed in');
        }
      });
    });

    return () => {
      cancelled = true;
      cancelIdle(idleHandle as number);
      unsubscribe?.();
    };
  }, []);

  return { user, isAuthenticated: !!user, loading, device };
}

export function AuthUserProvider({ children }: { children: ReactNode }) {
  const auth = useAuthUser();
  return (
    <AuthUserContext value={auth}>
      {children}
    </AuthUserContext>
  );
}

export function useAuthUserContext() {
  const context = useContext(AuthUserContext);
  if (!context) {
    throw new Error('useAuthUserContext must be used within an AuthUserProvider');
  }
  return context;
}